import { useCallback } from 'react';
import { useSearchParams } from 'react-router';

/**
 * @aiPurpose canonical tab list for the request drawer. Single source of
 *   truth — both the URL hook and `RequestDrawer.tsx` import from here so
 *   the union cannot drift between routing and rendering layers.
 * @aiInvariant order matches the visual tab order in `RequestDrawer.Tabs`.
 *   Adding a new tab requires updating both the array and any consumer
 *   that switches on `RequestDrawerTabId`.
 */
export const REQUEST_DRAWER_TABS = [
  'items',
  'carriers',
  'chat',
  'audit',
  'files',
  'archive',
] as const;

export type RequestDrawerTabId = (typeof REQUEST_DRAWER_TABS)[number];

const DEFAULT_TAB: RequestDrawerTabId = 'carriers';

// WHY (#2140): `snapshotId` is a drawer-owned param — the open return
//   snapshot on the archive tab. It is preserved across filter-owned URL
//   writes (alongside `openId`/`tab`) so a filter change does not close an
//   open snapshot.
const DRAWER_PARAM_KEYS = ['openId', 'tab', 'snapshotId'] as const;

/**
 * @aiPurpose copy drawer-owned keys (`openId`, `tab`, `snapshotId`) from a
 *   previous `URLSearchParams` snapshot onto the next one (issue #630,
 *   extended #2140). Filter-owned writers in `useRequestsFilters` MUST
 *   funnel through this helper instead of duplicating drawer-key knowledge,
 *   so the invariant "drawer state is exclusively mutated by
 *   `useRequestUrlState`" remains intact even as other writers compose new
 *   query strings around it.
 */
export function preserveRequestDrawerParams(
  prev: URLSearchParams,
  next: URLSearchParams,
): URLSearchParams {
  for (const k of DRAWER_PARAM_KEYS) {
    next.delete(k);
    const v = prev.get(k);
    if (v != null) next.set(k, v);
  }
  return next;
}

function isValidTab(value: string | null): value is RequestDrawerTabId {
  if (value === null) return false;
  return (REQUEST_DRAWER_TABS as readonly string[]).includes(value);
}

export interface RequestUrlStateOpts {
  replace?: boolean;
}

/**
 * @aiPurpose sole writer of `?openId=`, `?tab=`, and `?snapshotId=` query
 *   parameters on the `/requests` route. All other modules MUST go through
 *   this hook — direct `setSearchParams(...)` calls for these keys are
 *   forbidden so that history semantics, parameter preservation, and tab
 *   validation stay in one place. `snapshotId` (#2140) is the open return
 *   snapshot on the archive tab; it is drawer-owned.
 * @aiInvariant every method here clones the previous `URLSearchParams` and
 *   modifies only `openId` / `tab` / `snapshotId`. All other keys (filter `q`, `status`,
 *   `cursor`, `sort`, plus arbitrary tracking keys like `from=email`) are
 *   passed through untouched. `currentTab` falls back to `'carriers'` on
 *   missing or unknown `?tab=` value WITHOUT writing to the URL — pure
 *   read-side validation only. External writers (e.g. `useRequestsFilters`
 *   for issue #630) may compose new query strings around drawer keys
 *   provided they funnel through `preserveRequestDrawerParams` exported
 *   from this file; any other path that mutates `openId`/`tab` outside
 *   this hook is forbidden.
 */
export function useRequestUrlState() {
  const [searchParams, setSearchParams] = useSearchParams();

  const openRequest = useCallback(
    (id: string, tab?: RequestDrawerTabId, opts?: RequestUrlStateOpts) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('openId', id);
          next.set('tab', tab ?? DEFAULT_TAB);
          // WHY (#2140): a freshly opened request has no open snapshot.
          next.delete('snapshotId');
          return next;
        },
        opts?.replace ? { replace: true } : undefined,
      );
    },
    [setSearchParams],
  );

  const closeRequest = useCallback(
    (opts?: RequestUrlStateOpts) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('openId');
          next.delete('tab');
          next.delete('snapshotId');
          return next;
        },
        opts?.replace ? { replace: true } : undefined,
      );
    },
    [setSearchParams],
  );

  const switchTab = useCallback(
    (tab: RequestDrawerTabId, opts?: RequestUrlStateOpts) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', tab);
          // WHY (#2140): deliberately clear the open snapshot when leaving
          //   (or moving among) tabs — a snapshot belongs to the archive tab.
          next.delete('snapshotId');
          return next;
        },
        opts?.replace ? { replace: true } : undefined,
      );
    },
    [setSearchParams],
  );

  // WHY (#2140): open a frozen return snapshot — forces the archive tab and
  //   records the snapshot id. The archive tab renders the read-only view.
  const openSnapshot = useCallback(
    (snapshotId: string, opts?: RequestUrlStateOpts) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', 'archive');
          next.set('snapshotId', snapshotId);
          return next;
        },
        opts?.replace ? { replace: true } : undefined,
      );
    },
    [setSearchParams],
  );

  // WHY (#2140): "back to the live request" control — drop the open snapshot
  //   but stay on the archive tab (which then renders the snapshot list).
  const clearSnapshot = useCallback(
    (opts?: RequestUrlStateOpts) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('snapshotId');
          return next;
        },
        opts?.replace ? { replace: true } : undefined,
      );
    },
    [setSearchParams],
  );

  const rawTab = searchParams.get('tab');
  const currentTab: RequestDrawerTabId = isValidTab(rawTab) ? rawTab : DEFAULT_TAB;
  const currentOpenId = searchParams.get('openId');
  const currentSnapshotId = searchParams.get('snapshotId');

  return {
    openRequest,
    closeRequest,
    switchTab,
    openSnapshot,
    clearSnapshot,
    currentTab,
    currentOpenId,
    currentSnapshotId,
  };
}
